import CONSTANTS from '@picsio/db/src/constants';
import l10n from '../shared/strings';

// Single source of truth is `@picsio/db` (`constants.team.colorLabels`).
// The literals keep the app working on db versions that predate that release.
const FALLBACK_DEFAULTS = [
  {
    slug: 'red', name: 'red', hex: '#FF0000', order: 0,
  },
  {
    slug: 'yellow', name: 'yellow', hex: '#FFCC00', order: 1,
  },
  {
    slug: 'green', name: 'green', hex: '#66CC00', order: 2,
  },
  {
    slug: 'blue', name: 'blue', hex: '#3300FF', order: 3,
  },
  {
    slug: 'purple', name: 'purple', hex: '#9900FF', order: 4,
  },
];

export const COLOR_LABELS = CONSTANTS.team.colorLabels || {
  defaults: FALLBACK_DEFAULTS,
  slugs: FALLBACK_DEFAULTS.map(({ slug }) => slug),
  maxSlots: FALLBACK_DEFAULTS.length,
  maxNameLength: 30,
  noColor: 'nocolor',
  hexPattern: /^#[0-9A-F]{6}$/i,
};

export const DEFAULT_COLOR_LABELS = COLOR_LABELS.defaults;

const DEFAULT_LABEL_BY_SLUG = new Map(
  DEFAULT_COLOR_LABELS.map((label) => [label.slug, { ...label, title: label.name }]),
);

export const NO_COLOR_OPTION = {
  value: COLOR_LABELS.noColor,
  title: l10n.DETAILS.textNoColorLabel,
};

const channelLuminance = (value) => {
  const channel = value / 255;

  return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
};

/** Luminance at which black and white give the same WCAG contrast ratio. */
const CONTRAST_PIVOT = 0.179;

export const getContrastTextColor = (hex) => {
  const normalized = typeof hex === 'string' ? hex.replace('#', '') : '';
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return '#fff';

  const [red, green, blue] = [0, 2, 4].map(
    (offset) => parseInt(normalized.slice(offset, offset + 2), 16),
  );
  const luminance = 0.2126 * channelLuminance(red)
    + 0.7152 * channelLuminance(green)
    + 0.0722 * channelLuminance(blue);

  return luminance > CONTRAST_PIVOT ? '#000' : '#fff';
};

const normalizeStoredLabel = (label) => {
  const defaultLabel = DEFAULT_LABEL_BY_SLUG.get(label?.slug);
  if (!defaultLabel) return null;

  const trimmedName = typeof label.name === 'string' ? label.name.trim() : '';
  const hasValidHex = typeof label.hex === 'string' && COLOR_LABELS.hexPattern.test(label.hex);
  const name = trimmedName || defaultLabel.name;

  return {
    slug: defaultLabel.slug,
    name,
    hex: hasValidHex ? label.hex.toUpperCase() : defaultLabel.hex,
    order: Number.isInteger(label.order) ? label.order : defaultLabel.order,
    // Defaults are persisted with their name, so the flag has to survive a round trip.
    isDefaultName: name === defaultLabel.name,
  };
};

/**
 * Normalizes the stored team configuration to a sorted list of labels.
 * Teams that never customized their labels get the defaults lazily.
 */
export const getColorLabels = (storedLabels) => {
  if (Array.isArray(storedLabels)) {
    const seenSlugs = new Set();

    return storedLabels
      .map(normalizeStoredLabel)
      .filter((label) => {
        if (!label || seenSlugs.has(label.slug)) return false;
        seenSlugs.add(label.slug);
        return true;
      })
      .sort((first, second) => first.order - second.order)
      .map((label, order) => ({ ...label, order }));
  }

  if (storedLabels && typeof storedLabels === 'object') {
    return DEFAULT_COLOR_LABELS.map((label) => {
      const storedName = typeof storedLabels[label.slug] === 'string'
        ? storedLabels[label.slug].trim()
        : '';
      const name = storedName || label.name;

      return { ...label, name, isDefaultName: name === label.name };
    });
  }

  return DEFAULT_COLOR_LABELS.map((label) => ({ ...label, isDefaultName: true }));
};

/** Drops the derived flags so Redux and the stored configuration keep the same shape. */
export const toStoredColorLabels = (labels) => labels.map(({
  slug, name, hex, order,
}) => ({
  slug, name, hex, order,
}));

/**
 * Resolves a stored color slug to the team-configured display name for chips,
 * tooltips, and other read-only UI. The slug itself stays the filter/API value.
 */
export const getColorLabelDisplayName = (slug, labels = []) => {
  if (slug === COLOR_LABELS.noColor) return NO_COLOR_OPTION.title;

  const configured = labels.find((label) => label.slug === slug);
  if (configured?.name) return configured.name;

  return DEFAULT_LABEL_BY_SLUG.get(slug)?.name || slug;
};

/**
 * Prepares configured labels for pickers and filters: the configured name as the
 * title, always followed by the "no color" option.
 */
export const getColorOptions = (storedLabels) => [
  ...getColorLabels(storedLabels).map((label) => ({
    value: label.slug,
    name: label.name,
    background: label.hex,
    // Defaults used to prefix the tooltip with `Labeled "…"`, which made them look
    // different from custom names. Show the configured name in both cases.
    title: label.name,
  })),
  NO_COLOR_OPTION,
];
